import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CircleIcon from '@mui/icons-material/Circle';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  classes,
  Root,
} from '../../common-components/common-forms/edit-details/utils';
import closeIcon from '../../../../assets/accountSetting-assets/x-circle.svg';
import {
  createOrUpdateSubscription,
  getAllPricingPlanDetails,
  getLoggedInUserDetail,
  getPricingPlanDetails,
  updateUserDetails,
} from '../../../../services/userApi';
import { getLoggedInUser } from '../../../../store/userSlice';
import { ACCOUNT_INFO } from '../../../../constants/staticContent';
import Toast from '../../utils/Toast';
import ConfirmDialog from '../../utils/ConfirmDialog';
import { lightPalette } from '../../../../theme';

const OrganizationSubscription = ({
  setShowSubscriptionPopup,
  adminData,
  subscriptionDetails,
  activePlanDetails,
  setActivePlanDetails,
  getSubscriptionFromOrganizationId,
  setRefreshGrid,
}) => {
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNo: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [allPlanDetails, setAllPlanDetails] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(activePlanDetails);
  const [totalCredits, setTotalCredits] = useState(0);
  const [isPlanSelected, setIsPlanSelected] = useState(false);
  const [isDiscontinueDialogOpen, setIsDiscontinueDialogOpen] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState();

  const CloseEditInfo = () => {
    setShowSubscriptionPopup(false);
    setSelectedPlan(activePlanDetails);
    setIsPlanSelected(false);
  };

  const config = {
    headers: {
      'content-type': 'multipart/form-data',
    },
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataUserDetails = new FormData();

    try {
      setIsLoading(true);
      const response = await updateUserDetails({ config, formDataUserDetails });
      if (response && response.status === 200) {
        setIsLoading(false);
        const loggedInUser = await getLoggedInUserDetail();
        dispatch(getLoggedInUser(loggedInUser));

        setShowSubscriptionPopup(false);
        // setIsClosed(false);
        setErrors({});
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      return error;
    }
  };

  const hasAnyError = Object.values(errors).some((value) => value === true);

  useEffect(() => {
    const getActiveSubscription = subscriptionDetails?.subscriptions?.filter(
      (subscription) => subscription.status === 'active',
    );
    getActiveSubscription?.map((activeSubData) =>
      setActiveSubscription(activeSubData),
    );
  }, [subscriptionDetails.subscriptions]);

  const getActivePricingPlan = async () => {
    try {
      const response = await getPricingPlanDetails(activeSubscription?.plan_id);
      if (response?.data) {
        setActivePlanDetails(response?.data);
        setSelectedPlan(response?.data);
      }
    } catch (err) {
      return err;
    }
  };

  useEffect(() => {
    getActivePricingPlan();
  }, [activeSubscription?.plan_id]);

  const getAllPricingPlans = async () => {
    try {
      const response = await getAllPricingPlanDetails();
      setAllPlanDetails(response);
    } catch (err) {
      return err;
    }
  };

  const createOrUpdateOrganizationSubscription = async () => {
    if (!selectedPlan?.id) {
      Toast({ message: 'No selected plan to update', variant: 'warning' });
      return;
    }
    try {
      setIsLoading(true);
      await createOrUpdateSubscription(
        adminData[0]?.id,
        selectedPlan?.id,
        'active',
      );
      getSubscriptionFromOrganizationId();
      setIsPlanSelected(false);
      setShowSubscriptionPopup(false);
      setRefreshGrid((prev) => !prev);
      setIsLoading(false);
    } catch (err) {
      Toast({ message: 'Error updating subscription', variant: 'warning' });
      setIsLoading(false);
      return err;
    }
  };

  useEffect(() => {
    getAllPricingPlans();
  }, []);

  const handlePlanChange = (event, newValue) => {
    setSelectedPlan(newValue);
    setIsPlanSelected(true);
    if (newValue && newValue.credits && adminData[0]?.remaining_credits) {
      const total = newValue.credits + adminData[0]?.remaining_credits;
      setTotalCredits(total);
    } else {
      setTotalCredits(adminData[0]?.remaining_credits || 0);
    }
  };

  const renderStatusNote = (plan) => {
    if (!plan) return null;
    if (plan.status === 'inactive') {
      return (
        <Typography variant="body2">
          Note: This plan has been discontinued.
        </Typography>
      );
    }
  };

  const handleDiscontinueSelectedPlan = () => {
    setIsDiscontinueDialogOpen(true);
  };

  const handleCloseDiscontinueDialog = () => {
    setIsDiscontinueDialogOpen(false);
  };

  const handleConfirmDiscontinueDialogue = () => {
    createOrUpdateOrganizationSubscription();
    setIsDiscontinueDialogOpen(false);
  };

  return (
    <Root className={classes.root}>
      <Card
        className={`${hasAnyError ? classes.errorCardComponent : classes.CardComponent} ${classes.CardComponentOrgSubscription}`}
        sx={{ height: '100%' }}
      >
        <CardHeader
          className={classes.CardHeader}
          title={
            <Grid container alignItems="center">
              <Grid item xs={10}>
                <Typography variant="h4" className={classes.HeaderTypography}>
                  Edit Subscription Details
                </Typography>
              </Grid>
              <Grid item xs={2} align="right">
                <Avatar
                  onClick={CloseEditInfo}
                  src={closeIcon}
                  className={classes.CloseAction}
                />
              </Grid>
            </Grid>
          }
        />
        {isLoading ? (
          <Box className={classes.loaderBox}>
            <CircularProgress />
          </Box>
        ) : (
          <CardContent className={classes.CardContent}>
            <Box component="form" noValidate onSubmit={handleSubmit}>
              <FormControl fullWidth>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                        >
                          Organization Name :
                        </Typography>
                        <TextField
                          disabled
                          fullWidth
                          size="small"
                          className={classes.GridItemTextfield}
                          value={adminData[0]?.name ? adminData[0]?.name : ''}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                        >
                          Admin Name :
                        </Typography>
                        <TextField
                          disabled
                          fullWidth
                          size="small"
                          className={classes.GridItemTextfield}
                          value={`${adminData[0]?.users[0].first_name} ${adminData[0]?.users[0].last_name}`}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={5.85}>
                      <FormControl fullWidth>
                        <Box className={classes.GridItems}>
                          <Typography
                            variant="h5"
                            className={classes.GridItemsTypography}
                          >
                            Admin Email :
                          </Typography>
                          <TextField
                            disabled
                            fullWidth
                            size="small"
                            className={classes.GridItemTextfield}
                            value={`${adminData[0]?.users[0].email}`}
                          />
                        </Box>
                      </FormControl>
                    </Grid>
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                        >
                          Credits Available :
                        </Typography>
                        <TextField
                          disabled
                          fullWidth
                          size="small"
                          className={classes.GridItemTextfield}
                          value={adminData[0]?.remaining_credits}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                        >
                          Subscription Start Date :
                        </Typography>
                        <TextField
                          disabled
                          fullWidth
                          size="small"
                          className={classes.GridItemTextfield}
                          value={
                            moment
                              .utc(activeSubscription?.updated_at)
                              .local()
                              .format('DD-MM-YYYY HH:mm') || ''
                          }
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={5.85}>
                      <Box className={classes.GridItems}>
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                        >
                          Subscription End Date :
                        </Typography>
                        <TextField
                          disabled
                          fullWidth
                          size="small"
                          className={classes.GridItemTextfield}
                          value={
                            moment(activeSubscription?.updated_at)
                              .add(activePlanDetails?.expiry, 'days')
                              .local()
                              .format('DD-MM-YYYY HH:mm') || ''
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
                <Box className={classes.GridBox}>
                  <Grid container justifyContent="space-between">
                    <Grid item xs={12}>
                      <Box
                        className={classes.GridItems}
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        <Typography
                          variant="h5"
                          className={classes.GridItemsTypography}
                          sx={{
                            paddingBottom: '0px !important',
                            paddingRight: '10px',
                          }}
                        >
                          Plans:
                        </Typography>
                        <Autocomplete
                          sx={{ width: '100%' }}
                          options={allPlanDetails}
                          getOptionLabel={(option) => option.name}
                          value={selectedPlan}
                          onChange={handlePlanChange}
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <CircleIcon
                                style={{
                                  color:
                                    option.status === 'active'
                                      ? lightPalette.forestGreen.darkest
                                      : lightPalette.colorRed.main,
                                  fontSize: '11px',
                                  marginRight: '5px',
                                }}
                              />
                              {option.name}
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Change Plan"
                              variant="outlined"
                            />
                          )}
                          size="small"
                          clearOnEscape
                          clearIcon={
                            <IconButton
                              aria-label="clear"
                              sx={{ padding: '1px' }}
                            >
                              <ClearIcon sx={{ fontSize: '5px' }} />
                            </IconButton>
                          }
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </FormControl>
            </Box>

            <Box className={classes.noteTextBox}>
              {isPlanSelected && selectedPlan && (
                <Typography variant="body2">
                  {`Note: The selected plan provides ${selectedPlan.credits} credits, with 
  ${adminData[0]?.remaining_credits || 0} credits currently remaining. 
  This brings the total available credits to ${totalCredits}. 
  The plan will expire on ${moment().add(selectedPlan?.expiry, 'days').local().format('MMMM D, YYYY')}.`}
                </Typography>
              )}
              {isPlanSelected && selectedPlan && renderStatusNote(selectedPlan)}
            </Box>
            <Box className={`${classes.Box} ${classes.BoxUpdateStyle}`}>
              <Button
                className={classes.CancelButton}
                onClick={() => {
                  setShowSubscriptionPopup(false);
                  setSelectedPlan(activePlanDetails);
                  setIsPlanSelected(false);
                }}
              >
                <Typography variant="body1">
                  {ACCOUNT_INFO.BILLING_INFO.CANCEL_BUTTON}
                </Typography>
              </Button>
              <Button
                variant="contained"
                className={classes.UpdateButton}
                onClick={() => {
                  if (selectedPlan?.status === 'inactive') {
                    handleDiscontinueSelectedPlan();
                  } else {
                    createOrUpdateOrganizationSubscription();
                  }
                }}
              >
                <Typography variant="body1">
                  {ACCOUNT_INFO.BILLING_INFO.UPDATE_BUTTON}
                </Typography>
              </Button>
            </Box>
          </CardContent>
        )}
      </Card>
      {isDiscontinueDialogOpen ? (
        <ConfirmDialog
          open={isDiscontinueDialogOpen}
          onClose={handleCloseDiscontinueDialog}
          onConfirm={handleConfirmDiscontinueDialogue}
          askingMessage={ACCOUNT_INFO.BILLING_INFO.ASSIGN_WITH_DISCONTINUE_PLAN}
        />
      ) : null}
    </Root>
  );
};

const adminDataPropTypes = PropTypes.arrayOf(PropTypes.object);
const subscriptionDetailsPropTypes = PropTypes.arrayOf(PropTypes.object);
const activePlanDetailsPropTypes = PropTypes.arrayOf(PropTypes.object);

OrganizationSubscription.defaultProps = {
  adminData: [{}],
  subscriptionDetails: [{}],
  activePlanDetails: [{}],
};

OrganizationSubscription.propTypes = {
  setShowSubscriptionPopup: PropTypes.func.isRequired,
  adminData: adminDataPropTypes,
  subscriptionDetails: subscriptionDetailsPropTypes,
  activePlanDetails: activePlanDetailsPropTypes,
  setActivePlanDetails: PropTypes.func.isRequired,
  getSubscriptionFromOrganizationId: PropTypes.func.isRequired,
  setRefreshGrid: PropTypes.func.isRequired,
};

export default OrganizationSubscription;
